import type { Tile } from "../engine";
import { ROAD_CONNECT_E, ROAD_CONNECT_N, ROAD_CONNECT_S, ROAD_CONNECT_W } from "./roadConnectFlags";

export type CardinalNeighbors = {
  north: Tile | undefined;
  east: Tile | undefined;
  south: Tile | undefined;
  west: Tile | undefined;
};

export type TileCoord = { x: number; y: number };

export function mapHeight(tiles: readonly Tile[], width: number): number {
  return tiles.length / width;
}

export function cardinalNeighbors({
  tiles,
  width,
  x,
  y,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): CardinalNeighbors {
  const height = mapHeight(tiles, width);
  return {
    north: y > 0 ? tiles[(y - 1) * width + x] : undefined,
    east: x < width - 1 ? tiles[y * width + (x + 1)] : undefined,
    south: y < height - 1 ? tiles[(y + 1) * width + x] : undefined,
    west: x > 0 ? tiles[y * width + (x - 1)] : undefined,
  };
}

export function isDirectionInBounds({
  direction,
  x,
  y,
  width,
  height,
}: TileCoord & { direction: number; width: number; height: number }): boolean {
  if (direction & ROAD_CONNECT_N) return y > 0;
  if (direction & ROAD_CONNECT_E) return x < width - 1;
  if (direction & ROAD_CONNECT_S) return y < height - 1;
  if (direction & ROAD_CONNECT_W) return x > 0;
  return false;
}

export function isSeaTile(tile: Tile | undefined): boolean {
  return tile?.kind === "sea";
}

export function isLandTile(tile: Tile | undefined): boolean {
  if (tile === undefined) return false;
  return tile.kind !== "sea" && tile.kind !== "road";
}

export function neighborInDirection({
  direction,
  neighbors,
}: {
  direction: number;
  neighbors: CardinalNeighbors;
}): Tile | undefined {
  if (direction & ROAD_CONNECT_N) return neighbors.north;
  if (direction & ROAD_CONNECT_E) return neighbors.east;
  if (direction & ROAD_CONNECT_S) return neighbors.south;
  if (direction & ROAD_CONNECT_W) return neighbors.west;
  return undefined;
}

export function offsetInDirection({
  direction,
  x,
  y,
}: TileCoord & { direction: number }): TileCoord {
  if (direction & ROAD_CONNECT_N) return { x, y: y - 1 };
  if (direction & ROAD_CONNECT_E) return { x: x + 1, y };
  if (direction & ROAD_CONNECT_S) return { x, y: y + 1 };
  if (direction & ROAD_CONNECT_W) return { x: x - 1, y };
  return { x, y };
}

export function connectedRoadDirections(mask: number): readonly number[] {
  const directions: number[] = [];
  if (mask & ROAD_CONNECT_N) directions.push(ROAD_CONNECT_N);
  if (mask & ROAD_CONNECT_E) directions.push(ROAD_CONNECT_E);
  if (mask & ROAD_CONNECT_S) directions.push(ROAD_CONNECT_S);
  if (mask & ROAD_CONNECT_W) directions.push(ROAD_CONNECT_W);
  return directions;
}

export function perpendicularDirections(axisMask: number): readonly [number, number] {
  if (axisMask === (ROAD_CONNECT_N | ROAD_CONNECT_S)) {
    return [ROAD_CONNECT_W, ROAD_CONNECT_E];
  }
  return [ROAD_CONNECT_N, ROAD_CONNECT_S];
}
